/**
 * Campo de endereço da cotação com AUTOCOMPLETAR (operador, 05/10): sugestões
 * enquanto a pessoa digita, escolhe uma, pronto.
 *
 * - consulta só depois de uma pausa de 300 ms e com 3+ caracteres; a consulta
 *   anterior é cancelada quando sai outra (cada uma é cobrada pelo provedor);
 * - teclado: ↑/↓ andam na lista, Enter escolhe a destacada, Esc fecha a lista
 *   (sem fechar o diálogo); mouse também;
 * - token de SESSÃO por campo (placeFieldSession, @erp/shared): nasce na
 *   primeira consulta, vai junto com a escolha, e a consulta seguinte nasce
 *   com outro. Limpar o campo abandona a sessão;
 * - escolher preenche o campo e marca o ponto como escolhido (quem resolve o
 *   lugar é o servidor, no "Calcular"); digitar de novo volta ao texto livre;
 * - sem sugestões disponíveis (`unavailable`: Places API não habilitada, sem
 *   provedor, falha) a lista simplesmente não aparece, sem aviso: o campo
 *   continua sendo texto livre com geocodificação no "Calcular".
 *
 * Atribuição: dado da Places API fora de um mapa do Google leva "Google Maps"
 * (políticas da Places API: texto em sans-serif 400, cinza #5E5E5E, sem
 * quebra, sem tradução).
 */
import { useEffect, useId, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';
import {
  QUOTE_SUGGEST_MIN_CHARS,
  placeFieldSession,
  type QuotePlaceChoice,
  type QuotePlaceSuggestion,
  type QuoteSuggestResponse,
} from '@erp/shared';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';

export const SUGGEST_DEBOUNCE_MS = 300;

/** a consulta ao servidor; null = autocompletar desligado (sem provedor ou indisponível) */
export type SuggestFn = (
  input: string,
  sessionToken: string,
  signal: AbortSignal
) => Promise<QuoteSuggestResponse>;

const newToken = () => crypto.randomUUID();

export function AddressInput({
  id,
  value,
  placeholder,
  onText,
  onPick,
  suggest,
  onUnavailable,
}: {
  id: string;
  value: string;
  placeholder?: string;
  /** toda tecla: o texto livre (e o fim de uma escolha anterior) */
  onText: (text: string) => void;
  /** uma sugestão escolhida, com o token da sessão que ela encerra */
  onPick: (choice: QuotePlaceChoice) => void;
  suggest: SuggestFn | null;
  /** o servidor disse que não há sugestões agora: a tela desliga o autocompletar */
  onUnavailable: () => void;
}) {
  const listId = useId();
  const session = useRef(placeFieldSession(newToken));
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inflight = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<QuotePlaceSuggestion[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ok'>('idle');
  const [provider, setProvider] = useState<'google' | 'fake' | null>(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    inflight.current?.abort();
    inflight.current = null;
  };
  const close = () => {
    setOpen(false);
    setHighlight(-1);
  };
  useEffect(() => cancel, []);
  // autocompletar desligado no meio do caminho: nada pendente, nada aberto
  useEffect(() => {
    if (suggest) return;
    cancel();
    close();
    setItems([]);
    setStatus('idle');
  }, [suggest]);

  // Esc fecha a LISTA, não o diálogo: o Radix escuta o Esc no document (fase de
  // captura), então quem chega antes é a captura na window
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || document.activeElement !== inputRef.current) return;
      e.stopPropagation();
      e.preventDefault();
      close();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);

  function schedule(text: string) {
    cancel();
    const query = text.trim();
    if (query === '') session.current.reset(); // campo limpo: sessão abandonada
    if (!suggest || [...query].length < QUOTE_SUGGEST_MIN_CHARS) {
      close();
      setItems([]);
      setStatus('idle');
      return;
    }
    timer.current = setTimeout(async () => {
      timer.current = null;
      const ctrl = new AbortController();
      inflight.current = ctrl;
      setStatus('loading');
      setOpen(true);
      let res: QuoteSuggestResponse;
      try {
        res = await suggest(query, session.current.forQuery(), ctrl.signal);
      } catch {
        res = { status: 'unavailable' };
      }
      if (ctrl.signal.aborted) return;
      inflight.current = null;
      if (res.status === 'unavailable') {
        close();
        setItems([]);
        setStatus('idle');
        onUnavailable();
        return;
      }
      setItems(res.suggestions);
      setProvider(res.provider);
      setStatus('ok');
      setHighlight(-1);
      setOpen(document.activeElement === inputRef.current);
    }, SUGGEST_DEBOUNCE_MS);
  }

  function pick(s: QuotePlaceSuggestion) {
    cancel();
    onPick({ placeId: s.placeId, label: s.label, sessionToken: session.current.pick() });
    close();
    setItems([]);
    setStatus('idle');
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const n = items.length;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (n === 0) return;
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (e.key === 'ArrowDown' ? (h + 1) % n : h <= 0 ? n - 1 : h - 1));
    } else if (e.key === 'Enter') {
      if (open && highlight >= 0 && highlight < n) {
        e.preventDefault();
        pick(items[highlight]!);
      }
    } else if (e.key === 'Tab') {
      close();
    }
  }

  const showList = open && suggest !== null && (status === 'loading' || status === 'ok');
  const optionId = (i: number) => `${listId}-opcao-${i}`;

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        id={id}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={showList ? listId : undefined}
        aria-activedescendant={showList && highlight >= 0 ? optionId(highlight) : undefined}
        onChange={(e) => {
          onText(e.target.value);
          schedule(e.target.value);
        }}
        onKeyDown={onKeyDown}
        onFocus={() => {
          if (items.length > 0 || status === 'ok') setOpen(true);
        }}
        onBlur={close}
        className={cn(status === 'loading' && 'pr-8')}
      />
      {status === 'loading' ? (
        <span className="pointer-events-none absolute top-2.5 right-2.5" aria-hidden>
          <Spinner className="size-4" />
        </span>
      ) : null}
      {showList ? (
        <div
          className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
          data-testid={`${id}-sugestoes`}
          // rolar a lista não tira o foco do campo (o blur fecharia a lista)
          onMouseDown={(e) => e.preventDefault()}
        >
          <ul
            id={listId}
            role="listbox"
            aria-label="Sugestões de endereço"
            className="max-h-72 overflow-y-auto py-1"
          >
            {items.map((s, i) => (
              <li
                key={s.placeId}
                id={optionId(i)}
                role="option"
                aria-selected={i === highlight}
                // mousedown, não click: o blur do campo fecharia a lista antes
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(s);
                }}
                onMouseEnter={() => setHighlight(i)}
                className={cn(
                  'flex cursor-pointer items-start gap-2 px-3 py-2 text-sm',
                  i === highlight && 'bg-accent text-accent-foreground'
                )}
              >
                <MapPin className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                <span className="min-w-0">
                  <span className="block truncate font-medium">{s.main}</span>
                  {s.secondary ? (
                    <span className="block truncate text-xs text-muted-foreground">
                      {s.secondary}
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
            {status === 'loading' && items.length === 0 ? (
              <li className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                <Spinner className="size-3.5" /> Buscando endereços…
              </li>
            ) : null}
            {status === 'ok' && items.length === 0 ? (
              <li className="px-3 py-2 text-sm text-muted-foreground">
                Nenhum endereço encontrado — siga com o texto digitado.
              </li>
            ) : null}
          </ul>
          {provider === 'google' ? (
            <div className="flex justify-end border-t px-3 py-1.5">
              <span
                translate="no"
                aria-label="Google Maps"
                className="whitespace-nowrap text-xs text-[#5E5E5E]"
                style={{
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 400,
                  letterSpacing: 'normal',
                }}
              >
                Google Maps
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
